import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import type { PagedResponse } from '../../minutes/types'
import type {
  MinutePerson,
  PersonInput,
  PrincipleDecision,
  PrincipleDecisionQuery,
  PrincipleDecisionUpsert,
  PrincipleDocument,
} from '../types'

const BASE = '/management/principle-decisions'

/** Aktif bağımsız prensip kararları — açıklama araması + sayfalama (analiz 5.4.2). */
export async function getPrincipleDecisions(
  query: PrincipleDecisionQuery,
): Promise<PagedResponse<PrincipleDecision>> {
  const params: Record<string, string | number> = { page: query.page ?? 0, size: query.size ?? 20 }
  if (query.q?.trim()) params.q = query.q.trim()
  if (query.sort) params.sort = query.sort
  const { data } = await apiClient.get<PagedResponse<PrincipleDecision>>(BASE, { params })
  return data
}

/** Yeni prensip kararı (5.4.1) — 201 → oluşturulan kayıt (numara dahil). */
export async function createPrincipleDecision(
  body: PrincipleDecisionUpsert,
): Promise<PrincipleDecision> {
  const { data } = await apiClient.post<PrincipleDecision>(BASE, body)
  return data
}

/** Açıklama/Sorumlu güncelle (5.4.4) — optimistic lock modDate zorunlu. */
export async function updatePrincipleDecision(
  id: number,
  body: PrincipleDecisionUpsert,
): Promise<PrincipleDecision> {
  const { data } = await apiClient.put<PrincipleDecision>(`${BASE}/${id}`, body)
  return data
}

/** Soft-delete (5.4.5). 204. */
export async function deletePrincipleDecision(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`)
}

/** Bir prensip kararının aktif paydaşları (5.6). */
export async function getStakeholders(id: number): Promise<MinutePerson[]> {
  const { data } = await apiClient.get<MinutePerson[]>(`${BASE}/${id}/stakeholders`)
  return data
}

/** Paydaş ekle (5.5) — sicil bazında tekil. Güncel liste döner. */
export async function addStakeholders(
  id: number,
  stakeholders: PersonInput[],
): Promise<MinutePerson[]> {
  const { data } = await apiClient.post<MinutePerson[]>(`${BASE}/${id}/stakeholders`, {
    stakeholders,
  })
  return data
}

/** Paydaş çıkar (5.7). Güncel liste döner. */
export async function removeStakeholder(id: number, employeeId: string): Promise<MinutePerson[]> {
  const { data } = await apiClient.delete<MinutePerson[]>(
    `${BASE}/${id}/stakeholders/${employeeId}`,
  )
  return data
}

/**
 * İlke kararlarını Excel (.xlsx) olarak indirir.
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportPrincipleDecisions(q?: string, sort?: string): Promise<void> {
  await downloadXlsx(`${BASE}/export`, { q, sort }, 'ilke-kararlari.xlsx')
}

/**
 * Prensip kararının dosya ekleri (meta). İçerik değil: indirme/önizleme ortak ek ucundan
 * (`/management/attachments/{id}`) yapılır.
 */
export async function getPrincipleAttachments(id: number): Promise<PrincipleDocument[]> {
  const { data } = await apiClient.get<PrincipleDocument[]>(`${BASE}/${id}/attachments`)
  return data
}

/** Bir veya çok dosya ekler (multipart; alan adı `file`). Aksiyon tarafıyla aynı kalıp. */
export async function uploadPrincipleAttachments(id: number, files: File[]): Promise<void> {
  const form = new FormData()
  files.forEach((f) => form.append('file', f))
  await apiClient.post(`${BASE}/${id}/attachments`, form)
}

/** Dosyayı kaldırır (soft-delete). */
export async function deletePrincipleAttachment(id: number, attachmentId: number): Promise<void> {
  await apiClient.delete(`${BASE}/${id}/attachments/${attachmentId}`)
}
